import { useEffect, useMemo, useRef, useState } from 'react'

import type { SynopUser } from '~/data/types/user'
import { logger } from '~/debug/logger'
import { useAuth } from '~/hooks/useAuth'

import { useUser } from '../hooks/users'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { UserCacheRecord } from './storage/schema'

export type UsersDataManagerContextValue = {
  data: SynopUser[]
  isLoading: boolean
  isFetching: boolean
}

type UseCurrentUserOptions = {
  skip?: boolean
}

function useCurrentUser(options?: UseCurrentUserOptions) {
  const { user } = useAuth()
  const { user: currentUser, isLoading, isFetching } = useUser(user?.id, options)
  return { currentUser, isLoading, isFetching }
}

function useUsersPersistence(service: IndexedDbService, user?: SynopUser) {
  const [inMemoryUsers, setInMemoryUsers] = useState<SynopUser[]>([])
  const receivedApiDataRef = useRef(false)

  // Hydrate the users cache from IndexedDB
  useEffect(() => {
    const hydrateUsers = async () => {
      try {
        const records = await service.getAll('users')
        if (!receivedApiDataRef.current) {
          setInMemoryUsers(records.map((record) => record.payload))
        }
      } catch (error) {
        logger.error('Failed to hydrate users cache', error)
      }
    }

    void hydrateUsers()
  }, [service])

  // Persist the user to IndexedDB
  useEffect(() => {
    if (!user) return

    receivedApiDataRef.current = true
    setInMemoryUsers((previousUsers) => upsertUser(previousUsers, user))

    const record: UserCacheRecord = {
      id: user.id,
      payload: user,
      syncedAt: Date.now()
    }

    const persistUser = async () => {
      try {
        await service.put('users', record)
      } catch (error) {
        logger.error('Failed to persist user snapshot', error)
      }
    }

    void persistUser()
  }, [user, service])

  return inMemoryUsers
}

export default function useUsersDataManager(service: IndexedDbService) {
  const { currentUser, isLoading, isFetching } = useCurrentUser()

  const inMemoryUsers = useUsersPersistence(service, currentUser)

  return useMemo<UsersDataManagerContextValue>(
    () => ({
      data: inMemoryUsers,
      isLoading,
      isFetching
    }),
    [inMemoryUsers, isLoading, isFetching]
  )
}

function upsertUser(users: SynopUser[], nextUser: SynopUser) {
  if (!nextUser.id) return users

  const index = users.findIndex((user) => user.id === nextUser.id)
  if (index === -1) return [...users, nextUser]

  const nextUsers = [...users]
  nextUsers[index] = nextUser
  return nextUsers
}
